import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ThemeState {
  dark: boolean | any;
  mode: "dark" | "light";
}

const initialState: ThemeState = {
  dark: false,
  mode: "light",
};

export const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<boolean>) {
      state.dark = action.payload;
      state.mode = action.payload ? "dark" : "light";
    },
  },
});

// Action creators are generated for each case reducer function
export const { setTheme } = themeSlice.actions;

export const selectTheme = (state: any) => state.theme.dark;
export const selectThemeMode = (state: any) => state.theme.mode;

export default themeSlice.reducer;

// color design tokens export
export const tokens = (mode: any) => ({
  ...(mode === "dark"
    ? {
        brand: {
          100: "#005da9",
        },
        grey: {
          100: "#D9D9D9",
          200: "#7A7A7A",
          300: "#dee2e6",
          400: "#ced4da",
          500: "#adb5bd",
          600: "#505050",
          700: "#495057",
          800: "#333333",
          900: "#212529",
          1000: "rgba(153, 153, 153, 0.361)",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#34244c",
          500: "#1a1524",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#08b4e9",
          600: "#2990f9",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#c3c6fd",
          300: "#a4a9fc",
          400: "#868dfb",
          500: "#6870fa",
          600: "#535ac8",
          700: "#3e4396",
          800: "#2a2d64",
          900: "#151632",
        },
        purple: {
          default: "#1B1625",
        },
        gradient: {
          default:
            "linear-gradient(180deg, rgba(0,189,226,1) 0%, rgba(26,158,250,1) 20%, rgba(9,192,253,1) 53%, rgba(133,62,248,1) 100%)",
        },
        boxShadow: {
          default: "0px 0px 0px 0px rgb(0 0 0 / 10%)",
        },
      }
    : {
        grey: {
          100: "#62676c",
          200: "#343a40",
          300: "#495057",
          400: "#6c757d",
          500: "#adb5bd",
          600: "#ced4da",
          700: "#dee2e6",
          800: "#7A7A7A",
          900: "#C7C7C7",
          1000: "rgba(153, 153, 153, 0.361)",
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#fff", // manually changed
          500: "#f7f7f7",
          600: "#1F2A40",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#853ef8",
          600: "#2990f9",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#151632",
          200: "#2a2d64",
          300: "#3e4396",
          400: "#535ac8",
          500: "#6870fa",
          600: "#868dfb",
          700: "#a4a9fc",
          800: "#c3c6fd",
          900: "#e1e2fe",
        },
        purple: {
          default: "#853EF8",
        },
        gradient: {
          default:
            "linear-gradient(180deg, rgba(0,189,226,1) 0%, rgba(26,158,250,1) 20%, rgba(9,192,253,1) 53%, rgba(133,62,248,1) 100%)",
        },
        boxShadow: {
          default: "0px 10px 10px 0px rgb(0 0 0 / 3%)",
        },
      }),
});

//

// mui theme settings
export const themeSettings = (mode: any) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#fcfcfc",
            },
          }),
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      colors: colors.purple["default"],
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
      body1: {
        fontSize: 25,
      },
    },
  };
};
